import { Check, Copy, Phone, PlusCircle, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useMatterActionsOptional } from '@/components/contexts/MatterActionsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PhoneNumber {
  id: string;
  phone_number: string;
}

/**
 * Validates a phone number
 * @param phoneNumber The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
const isValidPhoneNumber = (phoneNumber: string): boolean => {
  // Allow empty strings (validation happens on blur)
  if (!phoneNumber || phoneNumber.trim() === '') return true;

  // Basic phone validation - at least 10 digits
  // Remove all non-digit characters for counting
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  return digitsOnly.length >= 10;
};

/**
 * Formats a phone number for display
 * @param phoneNumber The phone number to format
 * @returns The formatted phone number
 */
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Format based on number of digits
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }
  if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
    return `1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }

  // If doesn't match patterns, return as is but with some spacing
  return phoneNumber;
};

interface PhoneNumberFieldProps {
  phone: PhoneNumber;
  index: number;
  isEditing: boolean;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Individual phone number field component
 */
const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  phone,
  index,
  isEditing,
  onChange,
  onRemove,
}) => {
  const [isValid, setIsValid] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const matterActions = useMatterActionsOptional();

  // Validate on mount and when phone number changes
  useEffect(() => {
    if (phone.phone_number) {
      setIsValid(isValidPhoneNumber(phone.phone_number));
    } else {
      setIsValid(true);
    }
  }, [phone.phone_number]);

  if (isEditing) {
    return (
      <div className="flex w-full items-center">
        <Input
          value={phone.phone_number || ''}
          onChange={(e) => {
            onChange(index, e.target.value);
            if (isTouched) {
              setIsValid(isValidPhoneNumber(e.target.value));
            }
          }}
          onBlur={() => {
            setIsTouched(true);
            setIsValid(isValidPhoneNumber(phone.phone_number));
          }}
          placeholder="Enter phone number"
          className={cn(
            'h-7 border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-xs',
            !isValid &&
              isTouched &&
              'text-destructive border-destructive focus-visible:ring-destructive',
          )}
        />
        {!isValid && isTouched && (
          <div className="text-[10px] text-destructive ml-1">Invalid</div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="h-6 px-1.5 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
        >
          <Trash className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  // Format phone number for display
  const formattedPhone = formatPhoneNumber(phone.phone_number);

  // Handle click - open call modal or copy to clipboard
  const handleClick = () => {
    if (matterActions) {
      matterActions.openCallModal({ phoneNumber: phone.phone_number });
    } else {
      // Fallback to copy
      navigator.clipboard
        .writeText(phone.phone_number)
        .then(() => {
          setCopied(true);
          toast.success('Phone number copied to clipboard');
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          toast.error('Failed to copy');
        });
    }
  };

  // Get the appropriate icon
  const getIcon = () => {
    if (copied) {
      return <Check className="h-2.5 w-2.5 text-green-600" />;
    }
    return matterActions ? (
      <Phone className="h-2.5 w-2.5" />
    ) : (
      <Copy className="h-2.5 w-2.5" />
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group inline-flex items-center gap-1 text-left cursor-pointer text-xs text-gray-600"
      title={matterActions ? 'Click to call' : 'Click to copy'}
    >
      <span className="border-b border-dashed border-zinc-300 group-hover:border-zinc-500 group-hover:text-zinc-900 transition-colors">
        {formattedPhone}
      </span>
      <span className="opacity-0 group-hover:opacity-100 text-zinc-400 group-hover:text-zinc-600 transition-opacity">
        {getIcon()}
      </span>
    </button>
  );
};

interface PhoneNumberFieldsProps {
  phoneNumbers: PhoneNumber[];
  isEditing: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, value: string) => void;
}

/**
 * Component for displaying and editing phone numbers
 */
export const PhoneNumberFields: React.FC<PhoneNumberFieldsProps> = ({
  phoneNumbers,
  isEditing,
  onAdd,
  onRemove,
  onChange,
}) => {
  return (
    <div className="border-t border-gray-100">
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-3">
        <dt className="text-xs font-medium text-gray-900">
          <div className="flex justify-between items-center">
            <span>Phone Numbers</span>
            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onAdd}
                className="h-6 text-[10px] text-muted-foreground/60 hover:text-primary/80 px-1.5"
              >
                <PlusCircle className="mr-0.5 h-3 w-3" />
                <span className="hidden sm:inline">Add Phone</span>
              </Button>
            )}
          </div>
        </dt>
        <dd className="mt-0.5 text-xs sm:col-span-2 sm:mt-0">
          {phoneNumbers.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">
              No phone numbers added
            </div>
          ) : (
            <div className="space-y-2">
              {phoneNumbers.map((phone, index) => (
                <div
                  key={phone.id || index}
                  className={
                    isEditing
                      ? ''
                      : 'border-b border-gray-50 pb-1.5 last:border-b-0 last:pb-0'
                  }
                >
                  <PhoneNumberField
                    phone={phone}
                    index={index}
                    isEditing={isEditing}
                    onChange={onChange}
                    onRemove={onRemove}
                  />
                </div>
              ))}
            </div>
          )}
        </dd>
      </div>
    </div>
  );
};
